import { memo, useCallback, useMemo } from 'react';

import type { DateOnly } from '../../utils/dateOnly';
import { createDateOnlyFromDate } from '../../utils/dateOnly';
import { DateTimePicker, type DateTimePickerProps } from '../DateTimePicker';

// DateOnlyPicker default shortcuts - only "Today"
const DATE_ONLY_DEFAULT_SHORTCUTS = {
  Today: 'Today',
} as const;

export type DateOnlyPickerProps = {
  /** Current value of the date picker */
  value: DateOnly | null;
  /** Change event handler */
  onChange: (value: DateOnly | null) => void;
  /** Portalize component: @default: true */
  portalize?: boolean;
} & Omit<DateTimePickerProps, 'value' | 'onChange' | 'showTimePicker' | 'showMilliseconds'>;

/**
 * Date-only picker component that wraps DateTimePicker
 * Disables time selection and works with DateOnly types
 */
export const DateOnlyPicker = memo(function DateOnlyPicker({
  value,
  onChange,
  portalize = true,
  shortcuts = DATE_ONLY_DEFAULT_SHORTCUTS,
  ...props
}: DateOnlyPickerProps) {
  // Convert DateOnly to Date for DateTimePicker using local time to avoid timezone display issues
  const dateTimeValue = useMemo(() => {
    if (!value) {
      return null;
    }
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day); // Create local Date object (month is 0-based)
  }, [value]);

  // Convert Date back to DateOnly
  const handleChange = useCallback(
    (newDate: Date | null) => {
      if (newDate) {
        const dateOnly = createDateOnlyFromDate(newDate);
        onChange(dateOnly);
      } else {
        onChange(null);
      }
    },
    [onChange]
  );

  // Custom shortcut resolver for DateOnly shortcuts
  const customShortcutResolver = useCallback((shortcutClicked: string): Date | null => {
    if (shortcutClicked === 'Today') {
      // Return today's date at midnight UTC
      const today = new Date();
      return new Date(today.getFullYear(), today.getMonth(), today.getDate());
    }
    // For other shortcuts, return null to use default behavior
    return null;
  }, []);

  return (
    <DateTimePicker
      {...props}
      value={dateTimeValue}
      onChange={handleChange}
      showTimePicker={false}
      showMilliseconds={false}
      shortcuts={shortcuts}
      customShortcutResolver={customShortcutResolver}
      portalize={portalize}
    />
  );
});
